import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { allSettled } from 'rsvp';

import type { APIData, RequestError, Service, Slack, ValidationErrors } from 'webapp-types';
import { DeliveryType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { hashCode } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import CommonRecipientsSelectorComponent from 'webapp/components/common/recipients-selector';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormCompareDropdownComponent from 'webapp/components/form/compare/dropdown';
import FormRangeDropdownComponent from 'webapp/components/form/range/dropdown';
import FormScheduleComponent from 'webapp/components/form/schedule';
import NotificationsDeliveryTargetsComponent from 'webapp/components/notifications/delivery-targets';
import {
	type ChunkArgs as BaseChunkArgs,
	type ChunkInterface,
	type SaveFn,
	type SendPreviewFn,
	type ValidateFn,
} from 'webapp/components/notifications/edit/form/base';
import FormRowComponent from 'webapp/components/notifications/edit/form/row';
import RowErrorComponent from 'webapp/components/row-error';
import ScorecardMetricsComponent from 'webapp/components/scorecard/metrics';

import type { DataSelectRecord } from 'webapp/models';
import type { DataSelect, DateRange, DsCompare, Scorecard } from 'webapp/models-custom';

type ValidationKeys = 'delivery' | 'ds';
export type ValidationErrorsType = ValidationErrors<ValidationKeys>;

interface MainComponentArgs extends BaseChunkArgs {
	model: Scorecard;
	submitInProgress: boolean;
	errors: ValidationErrorsType;
	origin: string;
	setValidateFn?: (fn: ValidateFn) => void;
	setSaveFn?: (fn: SaveFn) => void;
	setSendPreviewFn?: (fn: SendPreviewFn) => void;
	onContentChange?: () => void;
}

interface MainComponentInterface extends ChunkInterface {
	Args: MainComponentArgs;
}

interface DataSelectChanges {
	dsToCreate: DataSelect[];
	dsToUpdate: DataSelect[];
	dsToDelete: DataSelect[];
}

export default class NotificationsEditFormScorecardMainComponent extends Component<MainComponentInterface> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/scorecards')
	declare mmScorecardsService: Service.ModelManager.Scorecards;

	@tracked model: Scorecard; // tracking here to detect changes

	previewSendingInProgress = false;

	constructor(owner: Owner, args: MainComponentArgs) {
		super(owner, args);

		this.model = this.args.model;

		this.args.setValidateFn?.(this.validate);
		this.args.setSaveFn?.(this.save);
		this.args.setSendPreviewFn?.(this.sendPreview);

		this.setupNewDataSelects();

		next(() => {
			this.fillEmptyLines();
			this.updateLimitationsFromDataSelects();
		});
	}

	get dataSelects() {
		return this.model.temporaryEditDataSelects;
	}

	set dataSelects(ds: DataSelect[]) {
		this.model.temporaryEditDataSelects = ds;
	}

	get isRecipientsShown() {
		return (
			(this.model.isActiveUserOwner && this.model.delivery.includes(DeliveryType.EMAIL)) ||
			this.model.delivery.includes(DeliveryType.PUSH)
		);
	}

	get setDefaultsOnDeliveryTargets() {
		return this.model.isNew && !this.model.cloneFrom;
	}

	@action
	onAddNewUsersToDeliveryChange(addNewUsersToDelivery: boolean) {
		this.model.addNewUsersToDelivery = addNewUsersToDelivery;
	}

	@action
	updateSlackChannels(newChannels: Slack.Channels, isFromSetDefaults = false) {
		this.analyticsService.event(TrackingEvent.SLACK_CHANNELS_SELECTED, { origin: 'scorecard' });
		this.model.slackChannels = newChannels as Slack.Channel[];
		if (isFromSetDefaults) {
			next(() => {
				this.args.model.applyBufferedChanges(['slackChannels']);
			});
		}
	}

	@action
	updateDelivery(delivery: number[], isFromSetDefaults = false) {
		this.model.delivery = delivery;
		if (isFromSetDefaults) {
			next(() => {
				this.args.model.applyBufferedChanges(['delivery']);
			});
		}

		this.args.resetErrorData?.('delivery');
	}

	// ---- Limitations & dateRanges START ----

	get rowMetricSetupsDataHash() {
		const props = {
			metricSetupDataItems: this.dataSelects.map((ds) => ds.metricSetupWithoutFiltersData),
		};
		return hashCode(JSON.stringify(props));
	}

	updateLimitationsFromDataSelects() {
		const validMetrics = this.dataSelects
			.map((ds) => ds.metricData)
			.filter((metricData) => metricData !== null);

		this.args.model.limitations?.changeMetrics(validMetrics);
	}

	@action
	handleMetricSetupKeyChange() {
		once(this, this.updateLimitationsFromDataSelects);
	}

	@action
	dateRangeChanged(dateRange: DateRange) {
		this.args.model.limitations.updateDateRangeFromOnlyRangeFormula(dateRange.rangeFormula);
		this.args.model.updateDateRangePlainObjFromCM();
	}

	@action
	compareChanged(compare: DsCompare) {
		const compareFormula = compare.getFormula();
		this.args.model.limitations.compare!.updateFromFormula(
			compareFormula,
			this.args.model.limitations.dateRange!,
		);
	}

	// ---- Limitations & dateRanges END ----

	// ---- Metric row handling START ----

	initialShownRows = 2; // Number of initial shown metric rows
	maxRows = 15; // Max number of metrics on scorecard

	get allFilled() {
		return !this.dataSelects.some((ds) => {
			return !ds.metricSetupKey;
		});
	}

	get isLastRowFilled() {
		const rowsCount = this.dataSelects.length;
		return !!this.dataSelects[rowsCount - 1]?.metricSetupKey;
	}

	@action
	moveItem(prevPosition: number, position: number) {
		const allDs = Array.from(this.dataSelects),
			movedMs = this.dataSelects[prevPosition]!,
			indexToRemove = allDs.indexOf(movedMs);

		allDs.splice(indexToRemove, 1);
		allDs.splice(position, 0, movedMs);

		// ugly way to force the changed array to correctly update components
		this.dataSelects = [];
		schedule('afterRender', () => {
			this.dataSelects = allDs;
		});
	}

	@action
	deleteItem(item: DataSelect) {
		this.dataSelects = this.dataSelects.filter((ds) => ds !== item);
		if (item.isNew) {
			void item.delete();
		}

		if (!this.dataSelects.length) {
			this.addEmptyDataSelect();
		}

		this.args.onContentChange?.();
	}

	@action
	fillEmptyLines() {
		// fill lines up to @initialShownRows.
		// only called on construct
		const linesToAdd = Math.max(0, this.initialShownRows - this.dataSelects.length),
			emptyLines = [];
		for (let i = 0; i < linesToAdd; i++) {
			emptyLines.push(this.prepEmptyDataSelect());
		}
		this.dataSelects = [...this.dataSelects, ...emptyLines];
		this.checkToAddNewRow();
	}

	@action
	checkToAddNewRowOnce() {
		once(this, this.checkToAddNewRow);
	}

	@action
	checkToAddNewRow() {
		if (this.isLastRowFilled && this.dataSelects.length < this.maxRows) {
			this.addEmptyDataSelect();
		}
	}

	private setupNewDataSelects() {
		const dataSelects = this.mmDataSelectsService.createModelsFromRecordsArray(
			this.model.savedDataSelects.map((ds) => ds.model),
		);
		this.model.temporaryEditDataSelects = dataSelects;
	}

	private addEmptyDataSelect() {
		const ms = this.prepEmptyDataSelect();
		this.dataSelects = [...this.dataSelects, ms];

		schedule('afterRender', () => {
			next(() => {
				this.args.onContentChange?.();
			});
		});
	}

	private prepEmptyDataSelect() {
		return this.mmScorecardsService.createDataSelectForScorecard(this.args.model);
	}

	private getCurrentDataSelects() {
		// validate & filter selection
		let currentDs = Array.from(this.dataSelects);

		// remove the ones that are totally empty (no metric selected)
		Array.from(currentDs).forEach((ds) => {
			if (!ds.metricSetupKey) {
				ds.rollback();
				currentDs = currentDs.filter((d) => d !== ds);
			}
		});

		return currentDs;
	}

	private getUpdatedDataSelects(asCopy: boolean): DataSelectChanges {
		const currentDs = this.getCurrentDataSelects();
		const savedDsRecords = this.args.model.savedDataSelects.map((ds) => ds.model);
		const removedDsRecords: DataSelectRecord[] = [];
		const dsToSave: DataSelect[] = [];
		const dsToDelete: DataSelect[] = [];

		// Remove last one if not fully filled
		if (!currentDs.slice(-1)[0]?.metricSetup.isFilledSingleOutput) {
			currentDs.pop();
		}

		// Find removedDsRecords items
		savedDsRecords.forEach((ds) => {
			if (!currentDs.map((m) => m.model).includes(ds)) {
				removedDsRecords.push(ds);
			}
		});

		// Update slots
		currentDs.forEach((ds, idx) => {
			ds.order = idx;
		});

		if (asCopy) {
			currentDs.forEach((ds) => {
				const newDs = this.mmDataSelectsService.createNewModel(ds.toJSON());
				dsToSave.push(newDs);
			});
		} else {
			removedDsRecords.forEach((ds) => {
				dsToDelete.push(this.mmDataSelectsService.createModelFromRecord(ds));
			});
			dsToSave.push(...currentDs);
		}

		return {
			dsToCreate: dsToSave.filter((ds) => !ds.id),
			dsToUpdate: dsToSave.filter((ds) => !!ds.id),
			dsToDelete,
		};
	}

	// ---- Metric row handling END ----

	@action
	validate() {
		const errors: ValidationErrorsType = {};

		const dsToValidate = this.getCurrentDataSelects();

		this.args.model.validate(); // to cleanup general errors

		if (!this.args.model.delivery.length) {
			errors.delivery = [{ message: 'Required' }];
		}

		if (dsToValidate.length === 0) {
			this.infolayerService.error('Scorecard must have at least 1 metric!');
			errors.ds = [{ message: '!!!' }];
		} else {
			dsToValidate.forEach((ds) => {
				const metricSetup = ds.metricSetup;
				delete ds.errors.attributes;
				delete ds.errors.units;
				if (!ds.validate() || !metricSetup.isFilledSingleOutput) {
					errors.ds = [{ message: '!!!' }];
					// showing the errors in Scorecard::MetricsRow
					if (metricSetup.metricHasDimensions && !metricSetup.dimensionFilterItemsCount) {
						ds.errors.attribute = [{ message: 'Dimension is required' }];
					} else if (metricSetup.hasUnits && !metricSetup.specificSelectedUnit) {
						ds.errors.unit = [{ message: 'Unit is required' }];
					}
				}
			});
		}

		return errors;
	}

	@action
	async save() {
		let scorecard = this.model;

		// Copy is made when scorecard is changed by other user and not owner - recipients are not copied but can be added after copy of scorecard is created.
		// Differ from other copy by property originateFrom (stored also to BE) that indicates scorecard was copied.
		const asCopyWhenReceivedScorecardIsEdited =
			!scorecard.isActiveUserOwner && this.hasDirtyAttributes();

		// Duplicating scorecards from scorecards landing page, all data is copied and editable (recipients, delivery time, delivery options).
		// Copy has property cloneFrom (not stored to BE) and indicates scorecard is clone just for the time of duplicating, afterwards its same like newly created scorecard.
		const asCopyWhenScorecardIsDuplicated = !!scorecard.isNew && !!scorecard.cloneFrom;

		// when creating copy we need to ensure that metricsettings are copied also.
		const copyDs = asCopyWhenReceivedScorecardIsEdited || asCopyWhenScorecardIsDuplicated;

		if (copyDs) {
			scorecard.blockId = null;
		}

		if (asCopyWhenReceivedScorecardIsEdited) {
			const title = scorecard.title;
			const data = scorecard.getDataForClone();

			data.title = title;
			data.userIds = [];
			data.addNewUsersToDelivery = false;
			delete data.cloneFrom; // used only when duplicating

			scorecard = this.mmScorecardsService.createNewModel(data);
		}

		// Ensure current user is included in userIds
		const currentUserId = this.currentUserService.numericId!;
		if (!scorecard.userIds.includes(currentUserId)) {
			scorecard.userIds = [...scorecard.userIds, currentUserId];
		}

		const dataSelectsUpdated = this.getUpdatedDataSelects(copyDs);

		// force save bcos BE needs tracking of scorecard update also when only metrics change
		const savedScorecard = await scorecard.save(true);

		// Update blockId on metrics
		const blockId = savedScorecard.blockId;
		dataSelectsUpdated.dsToUpdate.forEach((ds) => (ds.blockId = blockId!));
		dataSelectsUpdated.dsToCreate.forEach((ds) => (ds.blockId = blockId!));

		// Process deletes first
		const deletedPromiseResults = await allSettled(
			dataSelectsUpdated.dsToDelete.map((m) => m.delete()),
		);

		// Then updates
		const updatedPromiseResults = await allSettled(
			dataSelectsUpdated.dsToUpdate.map((m) => m.save()),
		);

		// Finally creates
		const createdPromiseResults = await allSettled(
			dataSelectsUpdated.dsToCreate.map((m) => m.save()),
		);

		// const promiseResults == [
		// 	{ state: 'fulfilled', value: 1 },
		// 	{ state: 'rejected', reason: Error },
		// 	{ state: 'rejected', reason: Error }
		// ];

		const errors: RequestError[] = [];

		[...deletedPromiseResults, ...updatedPromiseResults, ...createdPromiseResults].forEach(
			(a) => {
				if (a.state === 'rejected') {
					errors.push(a.reason as RequestError);
				}
			},
		);

		// Track analytics
		if (
			dataSelectsUpdated.dsToCreate.length &&
			!dataSelectsUpdated.dsToUpdate.length &&
			!dataSelectsUpdated.dsToDelete.length
		) {
			const metricSetupsToTrack = dataSelectsUpdated.dsToCreate
				.filter((item) => !!item.metricSetup)
				.map((item) => item.metricSetup);
			const metrics = metricSetupsToTrack.map((m) => m.metric?.name ?? '');
			const datasources = [
				...new Set(metricSetupsToTrack.map((m) => m.metric?.dataSourceTypeKey)),
			];
			this.analyticsService.event(TrackingEvent.SCORECARD_CREATED, {
				metrics,
				datasources,
				name: scorecard.title,
				frequency: scorecard.schedule.typeText,
				delivery: scorecard.deliveriesTexts,
				origin: this.args.origin,
			});
		} else {
			this.analyticsService.event(TrackingEvent.SCORECARD_MODIFIED, {
				frequency: scorecard.schedule.typeText,
				delivery: scorecard.deliveriesTexts,
				origin: this.args.origin,
			});
		}

		if (errors.length) {
			errors.forEach((errorData) => {
				this.requestService.showAllValidationErrors(errorData);
			});
		}

		next(() => {
			// Cleanup and close
			if (asCopyWhenReceivedScorecardIsEdited) {
				void this.args.model.delete(true);
			}
		});

		if (errors.length) {
			// already handled with showAllValidationErrors
			return Promise.reject();
		} else {
			scorecard.temporaryEditDataSelects = scorecard.savedDataSelects.map((ds) => {
				return this.mmDataSelectsService.createModelFromRecord(ds.model);
			});
		}
	}

	@action
	sendPreview() {
		if (this.previewSendingInProgress) {
			return;
		}

		const dataSelects = this.getCurrentDataSelects();

		if (!dataSelects.length) {
			this.infolayerService.error('Scorecard must have at least 1 metric!');
			return;
		}

		if (!this.currentUserService.isVerified) {
			this.infolayerService.error(
				"Scorecard preview can't be sent until you confirm your email.",
			);
			return;
		}

		const model = this.args.model;

		if (!model.hasDelivery) {
			this.infolayerService.error(
				'Select at least one delivery option to have a scorecard preview to be sent to.',
			);
			return;
		}

		const data: APIData.Scorecard.PreviewRequest = {
			scorecard: model.toSerializedJSON(),
			dataSelects: [],
		};

		const generateFakeId = () => {
			// these are not used for anything, only BE needs them
			return this.mmDataSelectsService.generateFakeUUID();
		};

		dataSelects.forEach((ds, idx) => {
			ds.order = idx;
			const dsData = ds.toSerializedJSON();
			dsData.id = generateFakeId();
			(dsData.compares || []).forEach((compare) => {
				compare.id = generateFakeId();
			});
			data.dataSelects.push(dsData);
		});
		const layer = this.infolayerService.loading('Sending scorecard preview...');
		this.previewSendingInProgress = true;
		this.requestService
			.api('POST', '/scorecards/preview', data)
			.then(
				function () {
					layer.ok('Scorecard preview sent!');
				},
				function (errorData) {
					layer.close();
					throw errorData;
				},
			)
			.finally(() => {
				this.previewSendingInProgress = false;
			})
			.catch(this.requestService.showGenericValidationError.bind(this.requestService));
	}

	@action
	checkModelChange() {
		const oldModel = this.model;
		const newModel = this.args.model;
		if (oldModel !== newModel) {
			this.model = newModel;
			this.setupNewDataSelects();
			oldModel.cleanupTemporaryEditDataSelects();
		}
	}

	override willDestroy() {
		super.willDestroy();
		this.model?.cleanupTemporaryEditDataSelects();
	}

	private hasDirtyAttributes() {
		let hasDirtyProperties = false;

		const currentDs = this.getCurrentDataSelects(),
			savedDs = this.args.model.savedDataSelects;

		const modelDirtyProps = this.args.model.dirtyProperties;
		// if the only dirty property is delivery, it is not considered to be dirty (scorecard doesn't get copied)
		if (modelDirtyProps && modelDirtyProps.length > 0) {
			if (modelDirtyProps.length > 1 || !modelDirtyProps.includes('delivery')) {
				hasDirtyProperties = true;
			}
		}

		return (
			currentDs.length !== savedDs.length ||
			currentDs.some((ds) => ds.isDirty) ||
			hasDirtyProperties
		);
	}

	<template>
		<div
			data-last-row-filled={{this.isLastRowFilled}}
			data-msd-hash={{this.rowMetricSetupsDataHash}}
			{{didUpdate this.checkModelChange @model}}
			{{didUpdate this.checkToAddNewRowOnce this.isLastRowFilled}}
			{{didUpdate this.handleMetricSetupKeyChange this.rowMetricSetupsDataHash}}
		/>
		<ScorecardMetricsComponent
			@model={{this.dataSelects}}
			@submitInProgress={{@submitInProgress}}
			@scorecardDateRange={{@model.dateRangeCM}}
			@scorecardCompare={{@model.compare}}
			@onMove={{this.moveItem}}
			@onDelete={{this.deleteItem}}
		/>
		<FormRowComponent @label="Show data for" @className="row--dropdowns-in-line">
			<div class="selector">
				<FormRangeDropdownComponent
					@metricLimitations={{this.model.limitations}}
					@onChange={{this.dateRangeChanged}}
				/>
			</div>
			<div class="selector">
				<label>Compared to</label>
				<FormCompareDropdownComponent
					@metricLimitations={{this.model.limitations}}
					@onChange={{this.compareChanged}}
				/>
			</div>
		</FormRowComponent>

		<FormScheduleComponent
			@model={{this.model}}
			@showOneOff={{false}}
			@submitInProgress={{@submitInProgress}}
		/>

		<NotificationsDeliveryTargetsComponent
			@selected={{this.model.delivery}}
			@slackChannels={{this.model.slackChannels}}
			@setDefaults={{this.setDefaultsOnDeliveryTargets}}
			@isDisabled={{@submitInProgress}}
			@onSelectionChange={{this.updateDelivery}}
			@onSlackChannelsChange={{this.updateSlackChannels}}
		>
			<RowErrorComponent @items={{@errors.delivery}} />
		</NotificationsDeliveryTargetsComponent>

		{{#if this.isRecipientsShown}}
			<FormRowComponent @label="Recipient(s)" @className="row--radiobuttons">

				<CommonRecipientsSelectorComponent
					@model={{this.model}}
					@submitInProgress={{@submitInProgress}}
				/>

			</FormRowComponent>

			{{#if this.currentUserService.isAdmin}}
				<FormRowComponent @label="" @className="row--checkboxes">
					<FormCheckboxComponent
						@selected={{this.model.addNewUsersToDelivery}}
						@label="Automatically send this scorecard to new users added to your account"
						@isDisabled={{@submitInProgress}}
						@onChange={{this.onAddNewUsersToDeliveryChange}}
					/>
				</FormRowComponent>

			{{/if}}

		{{/if}}
	</template>
}
